import { GlobalContext } from '../api/GlobalContext';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Table, Card, Container, Row } from 'reactstrap';
import Header from 'components/Headers/Header.js';
import NavigationPath from 'components/Misc/NavigationPath';
import { getFirstEmrNoteType } from '../components/Misc/utils';

const TABLE_COLUMNS = [
  { key: 'name', label: 'Name', width: '33%' },
  { key: 'usedBy', label: 'Times Used', width: '33%' },
  { key: 'lastUsed', label: 'Last Used', width: '33%' },
];

// Displays a list of templates in a table format with sorting and navigation capabilities

const TemplateLibraryCollections = () => {
  const { apiClient } = useContext(GlobalContext);
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const emr = queryParams.get('emr');
  const note = queryParams.get('note');
  const profession = queryParams.get('profession');

  const [config, setConfig] = useState([]);

  // Save current path for navigation
  useEffect(() => {
    sessionStorage.setItem(
      'Library-List-Path',
      location.pathname + location.search,
    );
  }, [location]);

  // Fetch data on component mount
  useEffect(() => {
    fetchMostUsedEmrOrNoteTemplates();
  }, []);

  /**
   * Fetches and processes template data from the API
   * Sorts the data by usage count
   */
  const fetchMostUsedEmrOrNoteTemplates = async () => {
    try {
      const data = await apiClient.searchConfigsLibrary(emr, note, profession);
      if (data?.configs) {
        const formattedData = formatConfigData(data.configs).sort(
          (a, b) => new Date(b.usedBy) - new Date(a.usedBy),
        );
        setConfig(formattedData);
      }
    } catch (error) {
      console.error('Error fetching template data:', error);
    }
  };

  const formatConfigData = (configs) => {
    return Object.keys(configs).flatMap((key) =>
      configs[key].map((config) => ({
        libraryConfigId: config.config_library_id,
        name: config.name,
        emr: config.emr,
        note: getFirstEmrNoteType(config, config?.name || 'Template'),
        lastUsed: config.last_copied_at
          ? new Date(config.last_copied_at).toLocaleDateString()
          : new Date().toLocaleDateString(),
        usedBy: config.usage_count,
      })),
    );
  };

  const handleRowClick = (configDetails) => {
    navigate(
      `/User/template/library/details?configId=${parseInt(configDetails.libraryConfigId)}&emr=${configDetails.emr}`,
    );
  };

  const renderTableHeader = () => (
    <thead>
      <tr>
        {TABLE_COLUMNS.map((column) => (
          <th key={column.key} style={{ width: column.width }}>
            {column.label}
          </th>
        ))}
      </tr>
    </thead>
  );

  const renderTableRows = () => (
    <tbody>
      {config.map((configDetails, index) => (
        <tr
          key={index}
          onClick={() => handleRowClick(configDetails)}
          style={{ cursor: 'pointer' }}
        >
          <td>{configDetails.name}</td>
          <td>{configDetails.usedBy}</td>
          <td>{configDetails.lastUsed}</td>
        </tr>
      ))}
    </tbody>
  );

  const generateBreadcrumbItems = () => {
    const storedPath = sessionStorage.getItem('Library-Browse-Path') || '';
    const storedParams = new URLSearchParams(storedPath.split('?')[1]);
    const selectedValue =
      storedParams.get('emr') ||
      storedParams.get('note') ||
      storedParams.get('profession') ||
      null;

    return [
      { name: 'Template Library', path: '/User/template/library' },
      {
        name: selectedValue,
        path: sessionStorage.getItem('Library-Browse-Path'),
      },
      {
        name: profession ? `${emr}-${profession}` : `${emr}-${note}`,
        path: location.pathname + location.search,
      },
    ];
  };

  return (
    <>
      <Header isLite={true} />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <NavigationPath paths={generateBreadcrumbItems()} />
              <div className="header-container">
                <h3>
                  {profession ? `${emr}-${profession}` : `${emr}-${note}`}
                </h3>
              </div>
              <div className="table-responsive">
                <Table striped className="custom-table">
                  {renderTableHeader()}
                  {renderTableRows()}
                </Table>
              </div>
            </Card>
          </div>
        </Row>
        <style>{`
                    /* Header Styles */
                    .header-container {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin: 20px;
                    }

                    /* Table Container */
                    .table-responsive {
                        overflow-x: auto;
                    }

                    /* Table Base Styles */
                    .custom-table {
                        width: 100%;
                        border-collapse: collapse;
                        table-layout: auto;
                    }

                    /* Table Cell Styles */
                    .custom-table th, 
                    .custom-table td {
                        padding: 10px;
                        text-align: center;
                        word-break: break-word;
                        min-width: 100px;
                    }

                    .custom-table th {
                        background: #f8f9fa;
                        font-weight: bold;
                    }

                    /* Responsive Styles */
                    @media (max-width: 768px) {
                        .table-responsive {
                            display: block;
                            width: 100%;
                        }

                        .custom-table th, 
                        .custom-table td {
                            font-size: 0.8rem;
                            padding: 8px;
                        }

                        .custom-table th:nth-child(3), 
                        .custom-table td:nth-child(3) { 
                            min-width: 90px;
                        }

                        .custom-table th:nth-child(4), 
                        .custom-table td:nth-child(4) { 
                            min-width: 80px;
                        }

                        .custom-table th, 
                        .custom-table td {
                            white-space: nowrap;
                        }
                    }
                `}</style>
      </Container>
    </>
  );
};

export default TemplateLibraryCollections;
